/**
 * Card Visibility Section
 * Allows showing/hiding cards for all players or individual players via a popover
 */

import { View, Text, Pressable, TouchableOpacity } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { Popover } from '../Popover'
import { teacherControlStyles as styles } from './styles'
import type { PlayerInfo, CardVisibilityMode } from './types'

export interface CardVisibilitySectionProps {
  isOpen: boolean
  onClose: () => void
  onOpen: () => void
  players: PlayerInfo[]
  currentMode: CardVisibilityMode
  visiblePlayerUserIds: string[]
  onShowAllPublic: () => void
  onShowAllPrivate: () => void
  onHideAll: () => void
  onTogglePlayerVisibility: (userId: string, currentlyVisible: boolean) => void
}

export function CardVisibilitySection({
  isOpen,
  onClose,
  onOpen,
  players,
  currentMode,
  visiblePlayerUserIds,
  onShowAllPublic,
  onShowAllPrivate,
  onHideAll,
  onTogglePlayerVisibility
}: CardVisibilitySectionProps) {
  // Determine button appearance based on current state
  const getButtonLabel = () => {
    if (currentMode === 'allPublic') return 'All Public'
    if (currentMode === 'allPrivate') return 'All Private'
    if (currentMode === 'byPlayer' && visiblePlayerUserIds.length > 0) {
      return `Cards (${visiblePlayerUserIds.length})`
    }
    return 'Show Cards'
  }

  const isActive = currentMode === 'allPublic' || currentMode === 'allPrivate' || visiblePlayerUserIds.length > 0

  const triggerButton = (
    <View style={[styles.button, isActive ? styles.activeButton : undefined]}>
      <Text style={[styles.buttonText, isActive && styles.activeButtonText]}>{getButtonLabel()}</Text>
    </View>
  )

  const popoverContent = (
    <View>
      <View style={styles.popoverHeader}>
        <TouchableOpacity onPress={onClose} hitSlop={8}>
          <Text style={styles.backArrow}>←</Text>
        </TouchableOpacity>
        <Text style={styles.popoverTitle}>Card Visibility</Text>
      </View>
      <ScrollView style={styles.recipientList} showsVerticalScrollIndicator={false}>
        {/* All Public option — everyone sees all cards */}
        <Pressable
          onPress={() => {
            onShowAllPublic()
            onClose()
          }}
          style={({ pressed }) => [
            styles.recipientItem,
            currentMode === 'allPublic' && styles.recipientItemActive,
            pressed && { opacity: 0.7 }
          ]}
        >
          <Text style={styles.recipientName}>All Public</Text>
          {currentMode === 'allPublic' && <Text style={styles.checkmark}>✓</Text>}
        </Pressable>
        {/* All Private option — only teacher sees all cards */}
        <Pressable
          onPress={() => {
            onShowAllPrivate()
            onClose()
          }}
          style={({ pressed }) => [
            styles.recipientItem,
            currentMode === 'allPrivate' && styles.recipientItemActive,
            pressed && { opacity: 0.7 }
          ]}
        >
          <Text style={styles.recipientName}>All Private</Text>
          {currentMode === 'allPrivate' && <Text style={styles.checkmark}>✓</Text>}
        </Pressable>
        {/* Hide All option */}
        <Pressable
          onPress={() => {
            onHideAll()
            onClose()
          }}
          style={({ pressed }) => [
            styles.recipientItem,
            currentMode === 'default' && styles.recipientItemActive,
            pressed && { opacity: 0.7 }
          ]}
        >
          <Text style={styles.recipientName}>Hide All</Text>
          {currentMode === 'default' && <Text style={styles.checkmark}>✓</Text>}
        </Pressable>
        {/* Divider */}
        <View style={styles.popoverDivider} />
        {/* Individual players */}
        {players.map((player) => {
          const isGlobalMode = currentMode === 'allPublic' || currentMode === 'allPrivate'
          const isVisible = isGlobalMode || visiblePlayerUserIds.includes(player.userId)
          return (
            <Pressable
              key={player._id}
              onPress={() => onTogglePlayerVisibility(player.userId, isVisible)}
              style={({ pressed }) => [
                styles.recipientItem,
                isVisible && !isGlobalMode && styles.recipientItemActive,
                pressed && { opacity: 0.7 }
              ]}
            >
              <Text style={styles.recipientIcon}>{player.kind === 'bot' ? '🤖' : '👤'}</Text>
              <Text style={styles.recipientName} numberOfLines={1}>
                {player.name}
              </Text>
              {isVisible && <Text style={styles.checkmark}>✓</Text>}
            </Pressable>
          )
        })}
      </ScrollView>
    </View>
  )

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      onTriggerPress={onOpen}
      trigger={triggerButton}
      side="top"
      align="end"
      sideOffset={12}
      contentStyle={styles.popoverContent}
    >
      {popoverContent}
    </Popover>
  )
}
